import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Calendar, Download, ExternalLink, FileQuestion, FileText, Hash, Image as ImageIcon, MapPin, Paperclip, Tag, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { api, fileUrl, fetchFileBlob } from "@/lib/api";

const formatExplorerDate = (value) => value ? new Date(value).toLocaleString("id-ID") : "-";

export default function ArchiveDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [activeKey, setActiveKey] = useState("main");

  useEffect(() => {
    let cancelled = false;

    async function fetchDoc() {
      setLoading(true);
      try {
        const { data } = await api.get(`/documents/${id}`);
        if (!cancelled) {
          setDoc(data);
          setActiveKey(data.storage_path ? "main" : `supporting-0`);
        }
      } catch (e) {
        if (!cancelled) toast.error("Dokumen tidak ditemukan.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchDoc();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const files = useMemo(() => {
    if (!doc) return [];

    const list = [];
    if (doc.storage_path) {
      list.push({
        key: "main",
        label: "Dokumen Utama",
        filename: doc.original_filename || "Dokumen utama",
        storage_path: doc.storage_path,
        content_type: doc.content_type,
      });
    }

    (doc.supporting_docs || []).forEach((item, index) => {
      if (!item.storage_path) return;
      list.push({
        key: `supporting-${index}`,
        label: item.category || "Dokumen Pendukung",
        filename: item.original_filename || `Dokumen pendukung ${index + 1}`,
        storage_path: item.storage_path,
        content_type: item.content_type,
      });
    });

    return list;
  }, [doc]);

  const activeFile = files.find((file) => file.key === activeKey) || files[0];

  const remove = async () => {
    await api.delete(`/documents/${id}`);
    toast.success("Dokumen dihapus dari arsip.");
    navigate("/arsip");
  };

  const downloadAll = async () => {
    setDownloadingAll(true);
    try {
      const response = await api.get(`/documents/${id}/download-zip`, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${safeFileName(doc.client_name || "arsip")}-${id}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal mengunduh arsip.");
    } finally {
      setDownloadingAll(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen p-4 pt-16 sm:p-8 lg:p-12 flex items-center justify-center">
        <div className="text-sm c-muted">Memuat detail arsip...</div>
      </div>
    );
  }

  if (!doc) {
    return (
      <div className="min-h-screen p-4 pt-16 sm:p-8 lg:p-12 flex items-center justify-center">
        <div className="c-card border rounded-xl p-8 text-center max-w-md">
          <FileQuestion className="w-12 h-12 mx-auto text-gold mb-3" strokeWidth={1.4} />
          <div className="font-display text-2xl c-strong mb-1">Dokumen tidak ditemukan</div>
          <p className="text-sm c-muted mb-5">Arsip mungkin sudah dihapus atau tautannya tidak valid.</p>
          <Link to="/arsip" className="inline-flex items-center justify-center gap-2 bg-gold text-white px-4 py-2.5 rounded-lg text-sm font-medium">
            <ArrowLeft className="w-4 h-4" /> Kembali ke Arsip
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 pt-16 sm:p-6 lg:p-8 animate-fade-up">
      <div className="mb-5 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <Link to="/arsip" className="inline-flex items-center gap-2 text-sm c-muted hover:text-gold transition-colors mb-4">
            <ArrowLeft className="w-4 h-4" /> Kembali ke Arsip
          </Link>
          <div className="text-[11px] uppercase tracking-[0.3em] text-gold mb-2">Detail Arsip</div>
          <h1 className="font-display text-3xl sm:text-4xl font-medium tracking-tight c-strong leading-tight">{doc.client_name}</h1>
          <p className="text-sm c-muted mt-1">{doc.document_type || "Dokumen Arsip"}</p>
        </div>

        {files.length > 0 && (
          <div className="flex flex-col sm:flex-row gap-2">
            <a href={fileUrl(activeFile.storage_path)} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 border c-border c-card px-4 py-2.5 rounded-lg text-sm c-strong hover:border-gold/50 hover:text-gold transition-colors">
              <ExternalLink className="w-4 h-4" /> Buka File
            </a>
            <button type="button" onClick={downloadAll} disabled={downloadingAll} data-testid="download-all-btn" className="inline-flex items-center justify-center gap-2 bg-gold text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-gold-dark transition-all shadow-lg shadow-gold/20 disabled:opacity-60">
              <Download className="w-4 h-4" /> {downloadingAll ? "Menyiapkan..." : "Unduh Semua"}
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_380px] gap-5">
        <section className="c-card border rounded-xl card-elev overflow-hidden min-h-[70vh]">
          <div className="border-b c-border p-3 sm:p-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.2em] text-gold mb-1">Pratinjau Realtime</div>
              <div className="text-sm font-medium c-strong truncate">{activeFile?.filename || "Tidak ada berkas"}</div>
            </div>

            {activeFile && (
              <div className="shrink-0 flex flex-wrap items-center gap-2">
                <button type="button" onClick={() => {
                  const explorerPath = (activeFile.storage_path || "").replace(/^notaris-arsip\/uploads\//, "");
                  navigate(`/explorer/edit?path=${encodeURIComponent(explorerPath)}`);
                }} data-testid="edit-active-btn" className="inline-flex items-center justify-center gap-2 rounded-lg border border-cyan-400 bg-cyan-400/10 px-3.5 py-2 text-xs font-medium text-cyan-200 hover:bg-cyan-400 hover:text-[#071016] transition-colors">
                  <ExternalLink className="w-3.5 h-3.5" /> {getExtension(activeFile) === "pdf" ? "Update File" : "Edit File"}
                </button>
                <button type="button" onClick={async () => {
                  try {
                    const blobUrl = await fetchFileBlob(activeFile.storage_path);
                    const a = document.createElement("a");
                    a.href = blobUrl;
                    a.download = activeFile.filename || "download";
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    URL.revokeObjectURL(blobUrl);
                  } catch { toast.error("Gagal mengunduh file."); }
                }} data-testid="download-active-btn" className="inline-flex items-center justify-center gap-2 rounded-lg border border-gold bg-gold/10 px-3.5 py-2 text-xs font-medium text-gold hover:bg-gold hover:text-white transition-colors">
                  <Download className="w-3.5 h-3.5" /> Download File Ini
                </button>
              </div>
            )}
          </div>

          <FileViewer file={activeFile} />
        </section>

        <aside className="space-y-5">
          <div className="c-card border rounded-xl p-5 card-elev">
            <div className="inline-block px-2.5 py-1 rounded-md bg-gold/10 text-gold text-[11px] uppercase tracking-[0.14em] mb-3">
              {doc.document_type || "Arsip"}
            </div>
            <h2 className="font-display text-2xl font-medium c-strong mb-5 leading-tight">{doc.client_name}</h2>

            <div className="space-y-1">
              <Meta icon={Hash} label="Nomor Akta" value={doc.deed_number || "-"} mono />
              <Meta icon={Calendar} label="Tanggal" value={doc.explorer_modified_at ? formatExplorerDate(doc.explorer_modified_at) : (doc.deed_date || "-")} mono />
              <Meta icon={MapPin} label="Daerah" value={doc.region || "-"} />
              <Meta icon={FileText} label="Di Upload Oleh" value={doc.uploaded_by_name || doc.owner_name || "-"} />
            </div>

            {doc.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-5">
                {doc.tags.map((tag, index) => (
                  <span key={index} className="inline-flex items-center gap-1 px-2.5 py-1 c-soft border c-border c-muted text-xs rounded-md">
                    <Tag className="w-3 h-3" /> {tag}
                  </span>
                ))}
              </div>
            )}

            {doc.notes && (
              <div className="mt-5">
                <div className="text-xs uppercase tracking-[0.12em] c-muted mb-1.5">Catatan</div>
                <p className="text-sm c-text leading-relaxed whitespace-pre-wrap">{doc.notes}</p>
              </div>
            )}
          </div>

          <div className="c-card border rounded-xl p-5 card-elev">
            <div className="flex items-center justify-between gap-3 mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-gold mb-1">Berkas</div>
                <div className="text-sm c-muted">{files.length} file tersedia</div>
              </div>
              <Paperclip className="w-5 h-5 text-gold" strokeWidth={1.5} />
            </div>

            <div className="space-y-2">
              {files.length > 0 ? files.map((file) => (
                <button
                  key={file.key}
                  type="button"
                  onClick={() => setActiveKey(file.key)}
                  className={`w-full text-left border rounded-lg px-3.5 py-3 transition-colors ${
                    activeFile?.key === file.key
                      ? "border-gold bg-gold/10"
                      : "c-border hover:border-gold/50"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <FileKindIcon file={file} />
                    <div className="min-w-0 flex-1">
                      <div className="text-xs text-gold mb-0.5">{file.label}</div>
                      <div className="text-sm c-strong truncate">{file.filename}</div>
                    </div>
                  </div>
                </button>
              )) : (
                <div className="border border-dashed c-border rounded-lg p-4 text-sm c-muted text-center">Tidak ada berkas untuk dipratinjau.</div>
              )}
            </div>
          </div>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button data-testid="delete-btn" className="w-full px-4 py-3 border border-wine/30 text-wine dark:text-[#E08585] dark:border-[#E08585]/30 rounded-lg text-sm hover:bg-wine/5 transition-colors flex items-center justify-center gap-2">
                <Trash2 className="w-4 h-4" /> Hapus Arsip
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent className="rounded-xl">
              <AlertDialogHeader>
                <AlertDialogTitle className="font-display text-2xl">Hapus dokumen ini?</AlertDialogTitle>
                <AlertDialogDescription>
                  Dokumen "{doc.client_name}" akan dihapus dari arsip digital. Tindakan ini tidak dapat dibatalkan.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="rounded-lg">Batal</AlertDialogCancel>
                <AlertDialogAction onClick={remove} data-testid="confirm-delete-btn" className="rounded-lg bg-wine hover:bg-wine/90 text-white">Hapus</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </aside>
      </div>
    </div>
  );
}

function FileViewer({ file }) {
  const storagePath = file?.storage_path || "";
  const url = storagePath ? fileUrl(storagePath) : "";
  const ext = file ? getExtension(file) : "";
  const editorRef = useRef(null);
  const previewMountRef = useRef(null);
  const isImage = file?.content_type?.startsWith("image/") || ["jpg", "jpeg", "png", "gif", "webp", "bmp", "tiff"].includes(ext);
  const isPdf = file?.content_type === "application/pdf" || ext === "pdf";
  const isOffice = ["doc", "docx", "xls", "xlsx", "ppt", "pptx"].includes(ext);
  const kindLabel = isImage ? "Gambar" : isPdf ? "PDF" : isOffice ? "Office Preview" : (ext || "File").toUpperCase();

  useEffect(() => {
    if (!isOffice || !file.storage_path || !previewMountRef.current) return undefined;
    let cancelled = false;
    const mount = previewMountRef.current;
    const host = document.createElement("div");
    const hostId = `onlyoffice-preview-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    host.id = hostId;
    host.style.width = "100%";
    host.style.height = "100%";
    mount.innerHTML = "";
    mount.appendChild(host);
    const explorerPath = (file.storage_path || "").replace(/^notaris-arsip\/uploads\//, "");
    const loadScript = (serverUrl) => new Promise((resolve, reject) => {
      if (window.DocsAPI) return resolve();
      const script = document.createElement("script");
      script.src = `${serverUrl}/web-apps/apps/api/documents/api.js`;
      script.onload = resolve;
      script.onerror = reject;
      document.body.appendChild(script);
    });
    (async () => {
      try {
        const { data } = await api.get("/onlyoffice/config", { params: { path: explorerPath } });
        if (cancelled) return;
        await loadScript(data.server_url || process.env.REACT_APP_ONLYOFFICE_SERVER_URL || "http://localhost:8082");
        if (cancelled) return;
        if (editorRef.current?.destroyEditor) editorRef.current.destroyEditor();
        editorRef.current = new window.DocsAPI.DocEditor(hostId, data.config);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "OnlyOffice preview belum siap.");
      }
    })();
    return () => {
      cancelled = true;
      if (editorRef.current?.destroyEditor) editorRef.current.destroyEditor();
      editorRef.current = null;
      if (host.parentNode === mount) mount.removeChild(host);
      else mount.innerHTML = "";
    };
  }, [file?.storage_path, isOffice]);

  if (!file) {
    return (
      <div className="h-[70vh] flex flex-col items-center justify-center text-center p-8">
        <FileQuestion className="w-16 h-16 text-gold/50 mb-4" strokeWidth={1.2} />
        <div className="font-display text-2xl c-strong mb-1">Tidak ada berkas</div>
        <p className="text-sm c-muted">Arsip ini belum memiliki file utama atau dokumen pendukung.</p>
      </div>
    );
  }

  if (isImage) {
    return (
      <div className="h-[72vh] bg-[radial-gradient(circle_at_top,#25313D_0,#0B1014_42%,#06090C_100%)] p-4 sm:p-6">
        <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-white shadow-2xl shadow-black/20 backdrop-blur">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-gold">Preview {kindLabel}</div>
            <div className="truncate text-sm font-medium">{file.filename}</div>
          </div>
          <a href={url} target="_blank" rel="noreferrer" className="shrink-0 rounded-lg border border-white/15 px-3 py-2 text-xs text-white/80 transition hover:border-gold hover:text-gold">
            Buka Penuh
          </a>
        </div>
        <div className="flex h-[calc(72vh-88px)] items-center justify-center overflow-auto rounded-2xl border border-white/10 bg-[linear-gradient(45deg,#111820_25%,#0f151c_25%,#0f151c_50%,#111820_50%,#111820_75%,#0f151c_75%,#0f151c)] bg-[length:24px_24px] p-4 shadow-inner">
          <img src={url} alt={file.filename} className="max-h-full max-w-full rounded-lg bg-white object-contain shadow-2xl shadow-black/50" />
        </div>
      </div>
    );
  }

  if (isOffice) {
    return <div className="h-[72vh] bg-[radial-gradient(circle_at_top,#25313D_0,#0B1014_45%,#06090C_100%)] p-4"><div ref={previewMountRef} className="h-full w-full overflow-hidden rounded-2xl border border-white/10 bg-[#0B1014] shadow-2xl shadow-black/35" /></div>;
  }

  if (isPdf) {
    return (
      <div className="h-[72vh] bg-[radial-gradient(circle_at_top,#25313D_0,#0B1014_42%,#06090C_100%)] p-4 sm:p-6">
        <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-white shadow-2xl shadow-black/20 backdrop-blur">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-gold">Preview PDF</div>
            <div className="truncate text-sm font-medium">{file.filename}</div>
          </div>
          <a href={url} target="_blank" rel="noreferrer" className="shrink-0 rounded-lg border border-white/15 px-3 py-2 text-xs text-white/80 transition hover:border-gold hover:text-gold">
            Buka Penuh
          </a>
        </div>
        <iframe src={url} title={file.filename} className="h-[calc(72vh-88px)] w-full rounded-2xl border border-white/10 bg-white shadow-2xl shadow-black/35" />
      </div>
    );
  }

  return (
    <div className="h-[72vh] bg-[radial-gradient(circle_at_top,#25313D_0,#0B1014_42%,#06090C_100%)] flex items-center justify-center p-6 text-center">
      <div className="max-w-md rounded-2xl border border-white/10 bg-white/[0.06] p-8 text-white shadow-2xl shadow-black/30 backdrop-blur">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gold/15 text-gold ring-1 ring-gold/25">
          <FileText className="w-8 h-8" strokeWidth={1.2} />
        </div>
        <div className="mb-1 text-[10px] uppercase tracking-[0.2em] text-gold">{kindLabel}</div>
        <div className="text-lg font-medium mb-2 break-words">{file.filename}</div>
        <p className="text-sm text-white/55 mb-5">Jenis file ini belum bisa dipratinjau langsung di browser.</p>
        <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 rounded-lg bg-gold px-4 py-2.5 text-sm font-medium text-white hover:bg-gold-dark transition-colors">
          <ExternalLink className="w-4 h-4" /> Buka File
        </a>
      </div>
    </div>
  );
}

function FileKindIcon({ file }) {
  const ext = getExtension(file);
  const isImage = file.content_type?.startsWith("image/") || ["jpg", "jpeg", "png", "gif", "webp", "bmp", "tiff"].includes(ext);

  if (isImage) {
    return <ImageIcon className="w-4 h-4 text-gold shrink-0 mt-0.5" strokeWidth={1.5} />;
  }

  return <FileText className="w-4 h-4 text-gold shrink-0 mt-0.5" strokeWidth={1.5} />;
}

function Meta({ icon: Icon, label, value, mono }) {
  return (
    <div className="flex items-center justify-between border-b c-border py-3 gap-4">
      <span className="flex items-center gap-2.5 c-muted text-sm">
        <Icon className="w-4 h-4 text-gold shrink-0" strokeWidth={1.6} /> {label}
      </span>
      <span className={`text-sm c-strong text-right ${mono ? "font-mono" : ""}`}>{value}</span>
    </div>
  );
}

function getExtension(file) {
  return (file?.filename || file?.storage_path || "").split(".").pop()?.toLowerCase() || "";
}

function safeFileName(value) {
  return String(value)
    .trim()
    .replace(/[<>:"/\\|?*\x00-\x1F]/g, "_")
    .replace(/\s+/g, "-")
    .replace(/^-+|-+$/g, "") || "arsip";
}
